import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MainTemplate } from 'templates';
import { WeeklyLiquidityChart } from 'organisms';
import {
  DateRangePresets,
  FakturowniaSyncChip,
  IDateRangeValue,
  presetRange,
} from 'molecules';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { DashboardKpis } from './DashboardKpis';
import { DashboardCharts } from './DashboardCharts';
import { InvoicesBreakdown } from './InvoicesBreakdown';

/**
 * Finance Dashboard (TZ §3.8 + §4.2) — landing for the «Финансы» menu group.
 * Date-range driven: every section refetches when from/to changes; sync chip
 * keeps data-freshness visible in the header.
 *
 * Phase 8 layout: KPI strip → charts (analytics first) → InvoicesBreakdown
 * (the concrete invoices behind every KPI in 5 tabs with count + sum
 * headers). PendingSection and OverdueList are gone — both are now tabs
 * inside Breakdown, which fixes the «numbers came from where?» complaint
 * by putting the verifying list right next to the KPI.
 */
export const FinanceDashboard = memo(() => {
  const { t } = useTranslation();
  const [range, setRange] = useState<IDateRangeValue>(() => ({
    preset: 'thisMonth',
    ...presetRange('thisMonth'),
  }));

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.financeDashboard.title')}</S.PageTitle>
          <S.HeaderRight>
            <FakturowniaSyncChip />
          </S.HeaderRight>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.financeDashboard.subtitle')}</S.SubTitle>

        <DateRangePresets value={range} onChange={setRange} />

        <DashboardKpis dateFrom={range.from} dateTo={range.to} />
        {/* Liquidity forecast teaser — fixed ±8-week horizon, clicks through
            to the payment calendar. */}
        <WeeklyLiquidityChart compact />
        <DashboardCharts dateFrom={range.from} dateTo={range.to} />
        <InvoicesBreakdown dateFrom={range.from} dateTo={range.to} />
      </S.Wrapper>
    </MainTemplate>
  );
});
